import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag } from 'lucide-react';
import DOMPurify from 'dompurify';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API } from '../App';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await API.get(`/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      setError('Post not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Render content: HTML from rich-text editor (sanitized) OR legacy markdown-like fallback
  const renderContent = (content) => {
    if (!content) return null;

    const trimmed = content.trim();
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(trimmed);

    if (looksLikeHtml) {
      const clean = DOMPurify.sanitize(trimmed, {
        USE_PROFILES: { html: true },
        ADD_ATTR: ['target', 'rel'],
      });
      return (
        <div
          className="blog-rich-content"
          dangerouslySetInnerHTML={{ __html: clean }}
        />
      );
    }

    // Legacy markdown-like fallback (for posts created before the rich editor)
    const lines = content.split('\n');
    const elements = [];
    let currentParagraph = [];

    lines.forEach((line, index) => {
      if (line.startsWith('## ')) {
        if (currentParagraph.length > 0) {
          elements.push(<p key={`p-${index}`} className="mb-6">{currentParagraph.join(' ')}</p>);
          currentParagraph = [];
        }
        elements.push(<h2 key={index} className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mt-10 mb-4">{line.replace('## ', '')}</h2>);
      } else if (line.startsWith('### ')) {
        if (currentParagraph.length > 0) {
          elements.push(<p key={`p-${index}`} className="mb-6">{currentParagraph.join(' ')}</p>);
          currentParagraph = [];
        }
        elements.push(<h3 key={index} className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mt-8 mb-3">{line.replace('### ', '')}</h3>);
      } else if (line.startsWith('- ')) {
        if (currentParagraph.length > 0) {
          elements.push(<p key={`p-${index}`} className="mb-6">{currentParagraph.join(' ')}</p>);
          currentParagraph = [];
        }
        elements.push(<li key={index} className="ml-6 mb-2 text-[#57534E]">{line.replace('- ', '')}</li>);
      } else if (line.trim() === '') {
        if (currentParagraph.length > 0) {
          elements.push(<p key={`p-${index}`} className="mb-6">{currentParagraph.join(' ')}</p>);
          currentParagraph = [];
        }
      } else {
        currentParagraph.push(line);
      }
    });

    if (currentParagraph.length > 0) {
      elements.push(<p key="final" className="mb-6">{currentParagraph.join(' ')}</p>);
    }

    return elements;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Header />
        <div className="pt-32 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#737373]">Loading post...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Header />
        <div className="pt-32 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-[#737373] text-lg mb-4">{error || 'Post not found'}</p>
            <Link to="/blog" className="text-[#2C5530] font-semibold hover:underline">
              Back to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-[#2C5530]" data-testid="post-hero">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <span className="flex items-center gap-2 text-white/70 text-sm">
              <Calendar className="w-4 h-4" />
              {formatDate(post.created_at)}
            </span>
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl md:text-5xl text-white font-medium mb-6">
            {post.title}
          </h1>
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, i) => (
                <span
                  key={i}
                  className="flex items-center gap-1 text-sm bg-white/10 text-white/80 px-3 py-1"
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Cover Image */}
      {post.cover_image && (
        <div className="container mx-auto px-6 md:px-12 max-w-4xl -mt-6">
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full aspect-[16/9] object-cover"
            data-testid="post-cover"
          />
        </div>
      )}

      {/* Content */}
      <article className="py-16 md:py-24" data-testid="post-content">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl">
          <div className="prose text-[#57534E] leading-relaxed text-lg">
            {renderContent(post.content)}
          </div>
        </div>
      </article>

      {/* CTA */}
      <section className="py-16 bg-[#F2F0E9]">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl text-center">
          <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-4">
            Ready to Work on Your Mental Game?
          </h3>
          <p className="text-[#57534E] mb-6">
            Let's discuss how I can help you perform with a quieter mind.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#2C5530] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-all duration-300"
            data-testid="post-cta"
          >
            Get in Touch
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
